import { AtSign, Share2, Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { memo } from 'react';
import { SITE } from '@/app/constants';
import type { ClawStatus } from '@/app/types';
import { formatLastModified, getMediaItemLabel, getMediaItemType, normalizePath } from '../../../_utils';
import type { ClawFileItem, InsertAssetPayload } from '../../../types';
import { getItemName } from '../../File/utils';
import { MediaAssetCard } from '../MediaAssetCard';
import { ActionButton } from './ActionButton';
import { MediaTypeIcon } from './MediaTypeIcon';
import { WatermarkDownloadButton } from './WatermarkDownloadButton';

interface MediaFileCardProps {
  item: ClawFileItem;
  clawId?: string;
  clawStatus: ClawStatus | null;
  sharing: boolean;
  deleting: boolean;
  watermarkDownloading?: boolean;
  onPreview: (path: string) => void;
  onInsert: (asset: InsertAssetPayload) => void;
  onShare: (item: ClawFileItem, type: 'image' | 'video') => void;
  onDelete: (item: ClawFileItem) => void;
  onDownload: (path: string) => void;
  onWatermarkDownload?: (path: string) => void;
  getCachedPreviewUrl: (path: string) => string | undefined;
  onResolvedPreviewUrl: (path: string, url: string) => void;
}

const MediaFileCardBase = ({
  item,
  clawId,
  clawStatus,
  sharing,
  deleting,
  watermarkDownloading,
  onPreview,
  onInsert,
  onShare,
  onDelete,
  onDownload,
  onWatermarkDownload,
  getCachedPreviewUrl,
  onResolvedPreviewUrl,
}: MediaFileCardProps) => {
  const t = useTranslations();
  const path = normalizePath(item.path);
  const itemType = getMediaItemType(item);
  const itemLabelType = getMediaItemLabel(item);
  const itemLabel = t(`claw.assets.filters.${itemLabelType}`);
  const itemLastModified = formatLastModified(item.last_modified);
  const itemName = getItemName(item);
  const isChina = SITE === 'china';

  const downloadButton = (
    <WatermarkDownloadButton
      showWatermarkOption={isChina && !!onWatermarkDownload && itemType !== 'file'}
      downloading={watermarkDownloading}
      onDownload={() => onDownload(path)}
      onWatermarkDownload={() => onWatermarkDownload?.(path)}
    />
  );

  return (
    <div className="overflow-hidden rounded-12 border border-system-white08 bg-system-white04">
      <div className="relative">
        <MediaAssetCard
          file={{
            file_path: path,
            file_name: itemName,
            file_type: itemType,
            preview_url: getCachedPreviewUrl(path),
          }}
          clawId={clawId}
          badge={
            <div
              className="pointer-events-none absolute top-2 left-2 z-10 flex size-9 items-center justify-center rounded-12 bg-system-black36 text-system-white shadow-[0_6px_18px_rgba(0,0,0,0.18)] backdrop-blur-sm"
              title={itemLabel}
            >
              <MediaTypeIcon type={itemLabelType} />
            </div>
          }
          onResolvedPreviewUrl={onResolvedPreviewUrl}
          onClick={() => onPreview(path)}
        />
      </div>
      <div className="flex items-center justify-between gap-3 border-system-white08 border-t px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-[11px] text-system-white24">{itemLastModified}</div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {itemType === 'image' || itemType === 'video' ? (
            <ActionButton
              tooltip={t('claw.assets.actions.share')}
              icon={<Share2 className="size-4" />}
              disabled={sharing}
              onClick={() => onShare(item, itemType)}
            />
          ) : null}
          {clawStatus === 'CLAW_STATUS_ACTIVE' ? (
            <ActionButton
              tooltip={t('claw.assets.actions.insert')}
              icon={<AtSign className="size-4" />}
              onClick={() =>
                onInsert({
                  path,
                  name: itemName,
                  type: itemType,
                  previewUrl: getCachedPreviewUrl(path),
                })
              }
            />
          ) : null}
          {downloadButton}
          <ActionButton
            tooltip={t('claw.assets.actions.delete')}
            icon={<Trash2 className="size-4" />}
            disabled={deleting}
            onClick={() => onDelete(item)}
          />
        </div>
      </div>
    </div>
  );
};

export const MediaFileCard = memo(MediaFileCardBase);
